import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  StandardizedTable,
  TableColumn,
  formatCurrency,
  formatNumber,
} from "@/components/ui/standardized-table";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ValorIndisponivel } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import type { TableQueryDTO } from "@/types/table-query";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import {
  getHistoricalGoogleAdsChartApiV1HistoricalGoogleAdsChartGet,
  getHistoricalGoogleAdsTableApiV1HistoricalGoogleAdsTableGet,
  getHistoricalMetaAdsChartApiV1HistoricalMetaAdsChartGet,
  getHistoricalMetaAdsTableApiV1HistoricalMetaAdsTableGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  HistoricalAdsChartItem as GeneratedHistoricalAdsChartItem,
  HistoricalGoogleAdsTableItem,
  HistoricalMetaAdsTableItem,
} from "@/backend-sdk/client/types.gen";

const formatCompactCurrency = (value: number) => {
  if (value >= 1000) {
    return `R$ ${(value / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} mil`;
  }
  return `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

type HistoricalChartPoint = {
  mes: string;
  investimento: number;
  receita: number;
};

type HistoricalGoogleAdsRow = HistoricalGoogleAdsTableItem;
type HistoricalMetaAdsRow = HistoricalMetaAdsTableItem;
type HistoricalAdsChartItem = GeneratedHistoricalAdsChartItem;

const mapHistoricalChartPoint = (
  item: HistoricalAdsChartItem,
): HistoricalChartPoint => ({
  mes: item.month_label,
  investimento: item.investment,
  receita: item.revenue,
});

const DualAxisChart: React.FC<{
  data: HistoricalChartPoint[];
  indexKey: keyof HistoricalChartPoint;
}> = ({ data, indexKey }) => (
  <ScrollableChart minWidth="500px">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={data}
        margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis
          dataKey={indexKey}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatCompactCurrency}
          width={80}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatCompactCurrency}
          width={80}
        />
        <RechartsTooltip
          contentStyle={{
            backgroundColor: "hsl(var(--popover))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "8px",
            fontSize: "12px",
          }}
          formatter={(value: number, name: string) => [
            formatCurrency(value),
            name === "investimento" ? "Investimento" : "Receita",
          ]}
          labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
        />
        <Legend
          formatter={(value) =>
            value === "investimento" ? "Investimento" : "Receita"
          }
          wrapperStyle={{ fontSize: "12px" }}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="investimento"
          stroke="hsl(var(--muted-foreground))"
          strokeWidth={2}
          dot={false}
          name="investimento"
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="receita"
          stroke="hsl(var(--destructive))"
          strokeWidth={2}
          dot={false}
          name="receita"
        />
      </ComposedChart>
    </ResponsiveContainer>
  </ScrollableChart>
);

/**
 * <auditoria 33> As visões da página, e por que ela deixou de ser só o filtro global.
 *
 * O Looker que a auditoria da Magnata usa como referência desenha jan/2023 a set/2026 **mês
 * a mês**; a página desenhava a janela do filtro global no grão que a escada do servidor
 * escolhe, e para os sete dias auditados isso é uma série diária de sete pontos. E não havia
 * janela que resolvesse: a escada topa em `month` aos 90 dias, então doze meses saíam em dois
 * pontos de semestre.
 *
 * As rotas de histórico passaram a aceitar `granularity` (M-245), e a página passou a ter uma
 * visão própria: mensal por padrão, ancorada no fim da janela do filtro. `filtro` devolve o
 * comportamento de antes — o *drill-down* que o item 33 manda preservar, e que é diário nas
 * janelas de até trinta dias. Ele não se chama "diário" porque quem decide o grão ali é a
 * escada do servidor, e uma janela de 120 dias sai em trimestres.
 *
 * ⚠️ **36 meses é o teto útil**, e não uma escolha de tela: o Graph do Meta recusa
 * `time_range.since` além de 37 meses. O servidor apara a janela em vez de perder a série
 * inteira, mas oferecer 48 meses aqui prometeria um passado que só o Google teria.
 */
const VISOES = [
  { valor: "12m", rotulo: "Últimos 12 meses", meses: 12 },
  { valor: "24m", rotulo: "Últimos 24 meses", meses: 24 },
  { valor: "36m", rotulo: "Últimos 36 meses", meses: 36 },
  { valor: "filtro", rotulo: "Período do filtro", meses: null },
] as const;

type ValorDeVisao = (typeof VISOES)[number]["valor"];

/** O primeiro dia da janela de `meses` que termina em `fim`, em `YYYY-MM-DD`. */
const inicioDaVisaoLonga = (fim: string, meses: number): string => {
  const data = new Date(`${fim}T00:00:00.000Z`);
  data.setUTCMonth(data.getUTCMonth() - meses);
  data.setUTCDate(data.getUTCDate() + 1);
  return data.toISOString().slice(0, 10);
};

/** Cada tabela lê uma mídia só: a de cima, o Google Ads; a de baixo, o Meta Ads. */
const FONTES_DO_GOOGLE: readonly RequisitoDeFonte[] = ["google_ads"];
const FONTES_DO_META: readonly RequisitoDeFonte[] = ["meta_ads"];

const HistoricoPage: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();
  const customerUuid =
    selectedAccount?.customer_uuid ||
    localStorage.getItem("active_customer_uuid") ||
    null;

  const PAGE_SIZE = 10;

  // As tabelas começam carregando: com `false`, o primeiro desenho — antes de o efeito
  // disparar a busca — mostrava a tabela vazia dizendo "Nenhum dado encontrado".
  const [googleTableLoading, setGoogleTableLoading] = useState(true);
  const [googleTableError, setGoogleTableError] = useState<string | null>(null);
  const [googleChartLoading, setGoogleChartLoading] = useState(false);
  const [googleChartError, setGoogleChartError] = useState<string | null>(null);
  const [metaTableLoading, setMetaTableLoading] = useState(true);
  const [metaTableError, setMetaTableError] = useState<string | null>(null);
  const [metaChartLoading, setMetaChartLoading] = useState(false);
  const [metaChartError, setMetaChartError] = useState<string | null>(null);

  const [googlePage, setGooglePage] = useState(1);
  const [googleTotal, setGoogleTotal] = useState(0);
  const [googleRows, setGoogleRows] = useState<HistoricalGoogleAdsRow[]>([]);
  const [dadosGraficoGoogleAds, setDadosGraficoGoogleAds] = useState<
    HistoricalChartPoint[]
  >([]);
  const [googleGranularity, setGoogleGranularity] = useState<string>("month");
  const [googleQuery, setGoogleQuery] = useState<TableQueryDTO>({});

  const [metaPage, setMetaPage] = useState(1);
  const [metaTotal, setMetaTotal] = useState(0);
  const [metaRows, setMetaRows] = useState<HistoricalMetaAdsRow[]>([]);
  const [dadosGraficoMetaAds, setDadosGraficoMetaAds] = useState<
    HistoricalChartPoint[]
  >([]);
  const [metaGranularity, setMetaGranularity] = useState<string>("month");
  const [metaQuery, setMetaQuery] = useState<TableQueryDTO>({});

  const [visao, setVisao] = useState<ValorDeVisao>("12m");

  const renderND = useCallback(
    (fontes: readonly RequisitoDeFonte[]) => (
      <ValorIndisponivel fontes={fontes} />
    ),
    [],
  );

  const resolveGranularityLabel = useCallback((granularity: string) => {
    switch (granularity) {
      case "day":
        return "Dia";
      case "quarter":
        return "Trimestre";
      case "semester":
        return "Semestre";
      case "month":
      default:
        return "Mês";
    }
  }, []);

  const isNil = useCallback(
    (value: unknown) => value === null || value === undefined,
    [],
  );

  const colunasGoogleAds: TableColumn[] = useMemo(
    () => [
      {
        key: "month_label",
        header: `Período (${resolveGranularityLabel(googleGranularity)})`,
        align: "left",
        type: "date",
        render: (value) => <span className="font-medium">{value}</span>,
      },
      {
        key: "clicks",
        header: "Cliques",
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_GOOGLE)
            : formatNumber(Number(value)),
      },
      {
        key: "conversions",
        header: "Conversões",
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_GOOGLE)
            : formatNumber(Number(value)),
      },
      {
        key: "cpa",
        header: "CPA",
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_GOOGLE)
            : formatCurrency(Number(value)),
      },
      {
        key: "conversion_rate",
        header: "Taxa Conversão",
        type: "percent",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_GOOGLE)
            : `${Number(value).toFixed(2)}%`,
      },
      {
        key: "average_order_value",
        header: "Ticket Médio",
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_GOOGLE)
            : formatCurrency(Number(value)),
      },
      {
        key: "investment",
        header: "Investimento",
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_GOOGLE)
            : formatCurrency(Number(value)),
      },
      {
        key: "roas",
        header: "ROAS",
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_GOOGLE)
            : Number(value || 0).toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
      },
      {
        key: "revenue",
        header: "Receita",
        heatmapColor: "green" as const,
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_GOOGLE)
            : formatCurrency(Number(value)),
      },
    ],
    [renderND, resolveGranularityLabel, googleGranularity, isNil],
  );

  const colunasMetaAds: TableColumn[] = useMemo(
    () => [
      {
        key: "month_label",
        header: `Período (${resolveGranularityLabel(metaGranularity)})`,
        align: "left",
        type: "date",
        render: (value) => <span className="font-medium">{value}</span>,
      },
      {
        key: "accesses",
        header: "Acessos",
        type: "number",
        render: (value) =>
          isNil(value) ? renderND(FONTES_DO_META) : formatNumber(Number(value)),
      },
      {
        key: "website_purchases",
        header: "Website Purchases",
        type: "number",
        render: (value) =>
          isNil(value) ? renderND(FONTES_DO_META) : formatNumber(Number(value)),
      },
      {
        key: "cpa",
        header: "CPA",
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_META)
            : formatCurrency(Number(value)),
      },
      {
        key: "conversion_rate",
        header: "TX Conversão",
        type: "percent",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_META)
            : `${Number(value).toFixed(2)}%`,
      },
      {
        key: "average_order_value",
        header: "TM",
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_META)
            : formatCurrency(Number(value)),
      },
      {
        key: "investment",
        header: "Investimento",
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_META)
            : formatCurrency(Number(value)),
      },
      {
        key: "roas",
        header: "ROAS",
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_META)
            : Number(value || 0).toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
      },
      {
        key: "revenue",
        header: "Receita",
        heatmapColor: "green" as const,
        type: "number",
        render: (value) =>
          isNil(value)
            ? renderND(FONTES_DO_META)
            : formatCurrency(Number(value)),
      },
    ],
    [renderND, resolveGranularityLabel, metaGranularity, isNil],
  );
  const buildRequestPayload = useCallback(() => {
    if (!filterState) {
      return null;
    }

    const endDate = formatDateToISO(filterState.dateRange.endDate);
    const comparison = filterState.comparison?.type || "previous_period";
    const customerUuid =
      localStorage.getItem("active_customer_uuid") ||
      selectedAccount?.customer_uuid ||
      selectedAccount?.id;
    const accountId = selectedAccount?.account_id;

    if (!customerUuid) {
      return null;
    }

    // <auditoria 33> A visão longa recua a partir do **fim** da janela do filtro, e não do
    // dia de hoje: o filtro continua dizendo "até quando", e o seletor diz "de quanto tempo
    // para trás". Sem `granularity`, o servidor cai na escada dele — o comportamento antigo.
    const meses = VISOES.find((opcao) => opcao.valor === visao)?.meses ?? null;

    return {
      startDate:
        meses === null
          ? formatDateToISO(filterState.dateRange.startDate)
          : inicioDaVisaoLonga(endDate, meses),
      endDate,
      comparison,
      customerUuid,
      accountId,
      granularity: meses === null ? undefined : ("month" as const),
    };
  }, [filterState, selectedAccount, visao]);

  const loadGoogleTable = useCallback(
    async (pageToLoad: number = 1) => {
      const payload = buildRequestPayload();
      if (!payload) {
        setGoogleRows([]);
        setGoogleTotal(0);
        setGoogleTableError("Cliente não selecionado");
        setGoogleTableLoading(false);
        return;
      }

      try {
        setGoogleTableLoading(true);
        setGoogleTableError(null);

        const { data: resp } =
          await getHistoricalGoogleAdsTableApiV1HistoricalGoogleAdsTableGet({
            query: {
              start_date: payload.startDate,
              end_date: payload.endDate,
              comparison: payload.comparison,
              customer_uuid: payload.customerUuid,
              account_id: payload.accountId,
              granularity: payload.granularity,
              page: pageToLoad,
              page_size: PAGE_SIZE,
            },
            throwOnError: true,
          });
        const items = resp.items || [];

        setGoogleRows(items);
        setGooglePage(resp.pagination?.page || pageToLoad);
        setGoogleTotal(resp.pagination?.total ?? items.length);
        if (resp.granularity) {
          setGoogleGranularity(resp.granularity);
        }
      } catch (err) {
        console.error("❌ Erro ao carregar tabela Google Ads:", err);
        setGoogleTableError(
          err instanceof Error ? err.message : "Erro ao carregar dados",
        );
        setGoogleRows([]);
        setGoogleTotal(0);
      } finally {
        setGoogleTableLoading(false);
      }
    },
    [buildRequestPayload],
  );

  const loadGoogleChart = useCallback(async () => {
    const payload = buildRequestPayload();
    if (!payload) {
      setDadosGraficoGoogleAds([]);
      setGoogleChartError("Cliente não selecionado");
      return;
    }

    try {
      setGoogleChartLoading(true);
      setGoogleChartError(null);

      const { data: resp } =
        await getHistoricalGoogleAdsChartApiV1HistoricalGoogleAdsChartGet({
          query: {
            start_date: payload.startDate,
            end_date: payload.endDate,
            comparison: payload.comparison,
            customer_uuid: payload.customerUuid,
            account_id: payload.accountId,
            granularity: payload.granularity,
          },
          throwOnError: true,
        });
      setDadosGraficoGoogleAds((resp.chart || []).map(mapHistoricalChartPoint));
      if (resp.granularity) {
        setGoogleGranularity(resp.granularity);
      }
    } catch (err) {
      console.error("❌ Erro ao carregar gráfico Google Ads:", err);
      setGoogleChartError(
        err instanceof Error ? err.message : "Erro ao carregar dados",
      );
      setDadosGraficoGoogleAds([]);
    } finally {
      setGoogleChartLoading(false);
    }
  }, [buildRequestPayload]);

  const loadMetaTable = useCallback(
    async (pageToLoad: number = 1) => {
      const payload = buildRequestPayload();
      if (!payload) {
        setMetaRows([]);
        setMetaTotal(0);
        setMetaTableError("Cliente não selecionado");
        setMetaTableLoading(false);
        return;
      }

      try {
        setMetaTableLoading(true);
        setMetaTableError(null);

        const { data: resp } =
          await getHistoricalMetaAdsTableApiV1HistoricalMetaAdsTableGet({
            query: {
              start_date: payload.startDate,
              end_date: payload.endDate,
              comparison: payload.comparison,
              customer_uuid: payload.customerUuid,
              account_id: payload.accountId,
              granularity: payload.granularity,
              page: pageToLoad,
              page_size: PAGE_SIZE,
            },
            throwOnError: true,
          });
        const items = resp.items || [];

        setMetaRows(items);
        setMetaPage(resp.pagination?.page || pageToLoad);
        setMetaTotal(resp.pagination?.total ?? items.length);
        if (resp.granularity) {
          setMetaGranularity(resp.granularity);
        }
      } catch (err) {
        console.error("❌ Erro ao carregar tabela Meta Ads:", err);
        setMetaTableError(
          err instanceof Error ? err.message : "Erro ao carregar dados",
        );
        setMetaRows([]);
        setMetaTotal(0);
      } finally {
        setMetaTableLoading(false);
      }
    },
    [buildRequestPayload],
  );

  const loadMetaChart = useCallback(async () => {
    const payload = buildRequestPayload();
    if (!payload) {
      setDadosGraficoMetaAds([]);
      setMetaChartError("Cliente não selecionado");
      return;
    }

    try {
      setMetaChartLoading(true);
      setMetaChartError(null);

      const { data: resp } =
        await getHistoricalMetaAdsChartApiV1HistoricalMetaAdsChartGet({
          query: {
            start_date: payload.startDate,
            end_date: payload.endDate,
            comparison: payload.comparison,
            customer_uuid: payload.customerUuid,
            account_id: payload.accountId,
            granularity: payload.granularity,
          },
          throwOnError: true,
        });
      setDadosGraficoMetaAds((resp.chart || []).map(mapHistoricalChartPoint));
      if (resp.granularity) {
        setMetaGranularity(resp.granularity);
      }
    } catch (err) {
      console.error("❌ Erro ao carregar gráfico Meta Ads:", err);
      setMetaChartError(
        err instanceof Error ? err.message : "Erro ao carregar dados",
      );
      setDadosGraficoMetaAds([]);
    } finally {
      setMetaChartLoading(false);
    }
  }, [buildRequestPayload]);

  useEffect(() => {
    if (!filterState) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadGoogleTable(1);
    loadGoogleChart();
    loadMetaTable(1);
    loadMetaChart();
  }, [
    filterState,
    selectedAccount,
    loadGoogleTable,
    loadGoogleChart,
    loadMetaTable,
    loadMetaChart,
    campaignFiltersNonce,
  ]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Histórico</h1>
          <p className="text-muted-foreground">
            Análise histórica de performance e métricas ao longo do tempo
          </p>
        </div>
      </div>

      <GlobalFilter
        rightSlot={
          <CampaignFiltersControl
            customerUuid={customerUuid}
            accountId={selectedAccount?.account_id}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />

      {/* <auditoria 33> A visão da série: mensal longa por padrão, o filtro como drill-down. */}
      <Card>
        <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:gap-4">
          <Label htmlFor="historico-visao" className="whitespace-nowrap">
            Visão da série
          </Label>
          <Select
            value={visao}
            onValueChange={(valor: ValorDeVisao) => {
              setVisao(valor);
              setGooglePage(1);
              setMetaPage(1);
            }}
          >
            <SelectTrigger id="historico-visao" className="sm:w-72">
              <SelectValue placeholder="Visão da série" />
            </SelectTrigger>
            <SelectContent>
              {VISOES.map((opcao) => (
                <SelectItem key={opcao.valor} value={opcao.valor}>
                  {opcao.rotulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {visao === "filtro"
              ? "As duas séries seguem a janela do filtro acima, no grão que o tamanho dela pedir — diário até trinta dias."
              : "As duas séries saem mês a mês, terminando no fim da janela do filtro acima."}
          </p>
        </CardContent>
      </Card>

      {/* Seção Google Ads */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-2xl font-semibold tracking-tight">Google Ads</h2>
        </div>
        {/* Sem esqueleto no lugar da tabela: desmontá-la a cada página apagava a busca e a
            ordenação da tela, enquanto a consulta ao servidor continuava com elas. */}
        {googleTableError ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-destructive">{googleTableError}</p>
            </CardContent>
          </Card>
        ) : (
          <StandardizedTable
            title="Dados Históricos de Performance - Google Ads"
            columns={colunasGoogleAds}
            data={googleRows}
            pagination={{
              current: googlePage,
              pageSize: PAGE_SIZE,
              total: googleTotal,
              onPageChange: (p) => loadGoogleTable(p),
            }}
            loading={googleTableLoading}
            queryMode="server"
            onQueryChange={(q) => {
              setGoogleQuery(q);
              setGooglePage(1);
            }}
          />
        )}

        {/* Gráfico de investimento e receita do Google Ads */}
        <Card>
          <CardHeader>
            <CardTitle>
              Evolução de Investimento e Receita do Google Ads
            </CardTitle>
          </CardHeader>
          <CardContent>
            {googleChartLoading ? (
              <Skeleton className="h-96 w-full" />
            ) : googleChartError ? (
              <p className="text-sm text-destructive">{googleChartError}</p>
            ) : (
              <div className="h-96">
                <DualAxisChart data={dadosGraficoGoogleAds} indexKey="mes" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Seção Meta Ads */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-2xl font-semibold tracking-tight">Meta Ads</h2>
        </div>
        {/* Sem esqueleto no lugar da tabela: desmontá-la a cada página apagava a busca e a
            ordenação da tela, enquanto a consulta ao servidor continuava com elas. */}
        {metaTableError ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-destructive">{metaTableError}</p>
            </CardContent>
          </Card>
        ) : (
          <StandardizedTable
            title="Dados Históricos de Performance - Meta Ads"
            columns={colunasMetaAds}
            data={metaRows}
            pagination={{
              current: metaPage,
              pageSize: PAGE_SIZE,
              total: metaTotal,
              onPageChange: (p) => loadMetaTable(p),
            }}
            loading={metaTableLoading}
            queryMode="server"
            onQueryChange={(q) => {
              setMetaQuery(q);
              setMetaPage(1);
            }}
          />
        )}

        {/* Gráfico de investimento e receita do Meta Ads */}
        <Card>
          <CardHeader>
            <CardTitle>
              Evolução de Investimento e Receita do Meta Ads
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metaChartLoading ? (
              <Skeleton className="h-96 w-full" />
            ) : metaChartError ? (
              <p className="text-sm text-destructive">{metaChartError}</p>
            ) : (
              <div className="h-96">
                <DualAxisChart data={dadosGraficoMetaAds} indexKey="mes" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HistoricoPage;
